import clsx from 'clsx';
import { Fragment, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import dropdownBlack from '../../../../../../assets/dropdown-black.svg';
import infoBlack from '../../../../../../assets/info.svg';
import { caretRightPurple } from '../../../../../../assets/hostedassets';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { CHIP_MEASURE_ATTR, useAutoFitChips } from '../../../../../../common/_custom/Hooks/useAutoFitChips';
import { useLatestRequestGuard } from '../../../../../../common/hooks/useLatestRequestGuard';
import { fetchBankStatementData } from '../../../../../../store/dashboard/bankStatement/apiHelper';
import {
    fetchBankEntityRationaleService,
    fetchBankStatementCashflowBucketsService,
    fetchBankTransactionsByBucketService,
    editClassificationsService,
    fetchOrgEntitiesService,
    OrgEntity,
    BucketPayload,
    EditClassificationEdit,
} from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { JAVA_API_STATUS } from '../../../../../../utils/axios-interceptor';
import { capitalizeWords } from '../../../../../../utils/utils';
import { useVisibilityConfig } from '../../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS, VISIBILITY_LEVEL } from '../../../../../../utils/constants/visibilityKeys';
import { getCashflowEditLevel } from '../../../../../../utils/visibilityConfigUtils';
import { DrawerBulkEdit } from './DrawerBulkEditFooter';
import EmptyBank from '../../../BankDataTable/components/EmptyBank/EmptyBank';
import { chipInfoForEntity, getSourceChipInfo, grainChipKey, isNoiseRationale } from './rationaleChips';
import {
    amountsToMap,
    buildCashflowAggregates,
    buildCashflowLayout,
    CashflowAggregates,
    formatScaledAmount,
    GroupRow,
    rowTotal,
} from './cashflowLayout';
import { EntityRationale, BankTransaction, ChipInfo, CashflowSubRow, DrawerState, StagedEdit, grainKey } from './types';
import {
    EMPTY_AGGREGATES,
    SPECIAL_LABEL_CLASS,
    LEAF_PAGE_SIZE,
    CELL_FONT,
    HEAD_FONT,
    CHIP_FONT,
    CELL_PADDING,
    WIDTH_BUFFER,
    PERIOD_MIN,
    PERIOD_MAX,
    TOTAL_MIN,
    TOTAL_MAX,
    NAME_COL_WIDTH,
    RATIONALE_MIN,
    RATIONALE_MAX,
    RATIONALE_CHIP_PRIORITY,
    RATIONALE_OVERFLOW_CHIP_RESERVE_PX,
    CLASSIFICATION_COL_WIDTH,
    VIEW_TXN_LABEL,
    VIEW_TXN_LABEL_EDIT,
} from './constants';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_CASHFLOW_BANKING_TRANSACTIONS_VIEWED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import EntityDrillDownDrawer from './EntityDrillDownDrawer';
import { EntityBubbleCell, RationaleCell, ClassificationCell } from './EntityRowEditControls';
import EditClassificationSaveBar from './EditClassificationSaveBar';
import ViewTxnArrowIcon from '../../../../../../assets/Svg/ViewTxnArrowIcon';
import styles from './FinancialsFromBankingV2.module.scss';

/** Display-only title-casing for leaf entity names. */
const toDisplayName = (name: string): string => capitalizeWords(name) ?? name;

/**
 * Extracts the rationale component from a kind-1 composite bucketKey.
 * Format: `<sorted-entityId-CSV>\x01<rationale>`. Splits on the first SOH delimiter.
 * Backward-safe: if no delimiter is present, the whole key is treated as the rationale.
 */
const parseRationaleFromBucketKey = (bucketKey: string): string => {
    const sep = bucketKey.indexOf('\x01');
    return sep === -1 ? bucketKey : bucketKey.slice(sep + 1);
};

// Column widths are measured from the rendered strings and handed to one shared <colgroup> so the sticky header and every per-section body table stay pixel-aligned.

/** Text width via a cached canvas context. Returns 0 where canvas is unavailable (jsdom). */
const measureText = (() => {
    let ctx: CanvasRenderingContext2D | null | undefined;
    return (text: string, font: string): number => {
        if (ctx === undefined) ctx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
        if (!ctx) return 0;
        ctx.font = font;
        return ctx.measureText(text).width;
    };
})();

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Leaf name cell: shows a tooltip with the full name only while it's actually ellipsis-clipped. Re-measured on hover, since the View Transactions CTA reveal changes the available width. */
function LeafName({ text, className }: { text: string; className?: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const [clipped, setClipped] = useState(false);

    const measure = useCallback(() => {
        const el = ref.current;
        if (el) setClipped(el.scrollWidth > el.clientWidth);
    }, []);

    useLayoutEffect(() => {
        measure();
        if (typeof ResizeObserver === 'undefined' || !ref.current) return;
        const ro = new ResizeObserver(measure);
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, [text, measure]);

    // content='' keeps the wrapper (and so the layout) identical whether or not the bubble shows.
    return (
        <Tooltip content={clipped ? text : ''} direction="top" usePortal fitContent>
            <span ref={ref} className={className} onMouseEnter={measure}>
                {text}
            </span>
        </Tooltip>
    );
}

/** Sort leaf sub-rows: 'other' residual last; all others by |total| descending, then name ascending. */
function sortedSubRows(subRows: CashflowSubRow[]): CashflowSubRow[] {
    return [...subRows].sort((a, b) => {
        const aIsOther = a.bucketType === 'other';
        const bIsOther = b.bucketType === 'other';
        if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
        const diff = Math.abs(rowTotal(b.amountsMap)) - Math.abs(rowTotal(a.amountsMap));
        if (diff !== 0) return diff;
        return (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase());
    });
}

type RationaleChipKind = 'edited' | 'citation' | 'lender' | 'fallback';
interface RationaleChip {
    kind: RationaleChipKind;
    label: string;
}

/** Tagging Rational chips in reading order. */
function rationaleChips(edited: boolean, chipInfo: ChipInfo | null, fallbackLabel: string | null): RationaleChip[] {
    const chips: RationaleChip[] = [];
    if (edited) chips.push({ kind: 'edited', label: 'edited' });
    if (chipInfo) {
        chips.push({ kind: 'citation', label: chipInfo.label });
        if (chipInfo.isLender) chips.push({ kind: 'lender', label: 'KNOWN LENDER' });
    }
    if (fallbackLabel) chips.push({ kind: 'fallback', label: fallbackLabel });
    return chips;
}

function renderRationaleChip(chip: RationaleChip, chipInfo: ChipInfo | null, opts: { measure?: boolean; nested?: boolean }): ReactNode {
    const measureAttr = opts.measure ? { [CHIP_MEASURE_ATTR]: '' } : {};
    if (chip.kind === 'edited' || chip.kind === 'fallback') {
        return (
            <span key={chip.kind} className={styles.Chip} title={chip.kind === 'edited' ? 'Edited' : chip.label} {...measureAttr}>
                <span className={styles.ChipLabel}>{chip.label}</span>
            </span>
        );
    }
    const tooltipContent =
        chip.kind === 'lender' || !chipInfo ? (
            'Name match from Known Lender List'
        ) : (
            <div className={styles.ChipTooltip}>
                <div>{chipInfo.hoverPayload.entityRationale}</div>
                {chipInfo.hoverPayload.primaryDocName && (
                    <div className={styles.ChipTooltipDoc}>{chipInfo.hoverPayload.primaryDocName}</div>
                )}
                {chipInfo.hoverPayload.allCitations.length > 1 && (
                    <div className={styles.ChipTooltipMore}>
                        {chipInfo.hoverPayload.allCitations.slice(1).map((c, i) => (
                            <div key={i}>{[c.doc_type, c.doc_name].filter(Boolean).join(' — ')}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    return (
        <Tooltip
            key={chip.kind}
            content={tooltipContent}
            direction="top"
            usePortal
            fitContent
            wrapperClassName={styles.ChipTooltipWrapper}
            disablePortalShowDispatch={opts.nested}
        >
            <span className={styles.Chip} {...measureAttr}>
                <span className={styles.ChipLabel}>{chip.label}</span>
                <img src={infoBlack} alt="" className={styles.ChipInfoIcon} />
            </span>
        </Tooltip>
    );
}

/** One row's Tagging Rational chips: those that fit whole on one line, then a "+N" chip listing the rest on hover. */
function RationaleChipStrip({ chips, chipInfo }: { chips: RationaleChip[]; chipInfo: ChipInfo | null }) {
    const { chipRowRef, visibleChipIndices } = useAutoFitChips(chips.length > 1, chips.length, {
        moreLabelWidthPx: RATIONALE_OVERFLOW_CHIP_RESERVE_PX,
        skipNonFitting: true,
    });
    const byPriority = [...chips].sort((a, b) => RATIONALE_CHIP_PRIORITY.indexOf(a.kind) - RATIONALE_CHIP_PRIORITY.indexOf(b.kind));

    if (visibleChipIndices === null && chips.length > 1) {
        return (
            <div ref={chipRowRef} className={clsx(styles.RationaleChips, styles.RationaleChipsMeasuring)}>
                {byPriority.map((chip) => renderRationaleChip(chip, chipInfo, { measure: true }))}
            </div>
        );
    }

    const keep = new Set((visibleChipIndices ?? byPriority.map((_, index) => index)).map((index) => byPriority[index].kind));
    const hidden = chips.filter((chip) => !keep.has(chip.kind));
    return (
        <div ref={chipRowRef} className={styles.RationaleChips}>
            {chips.filter((chip) => keep.has(chip.kind)).map((chip) => renderRationaleChip(chip, chipInfo, { measure: true }))}
            {hidden.length > 0 && (
                <Tooltip
                    content={
                        <div className={styles.ChipOverflowList}>
                            {hidden.map((chip) => renderRationaleChip(chip, chipInfo, { nested: true }))}
                        </div>
                    }
                    direction="top"
                    usePortal
                    fitContent
                    hideDelay={300}
                    focusable
                    wrapperClassName={styles.ChipOverflowWrapper}
                    ariaLabel={`${hidden.length} more: ${hidden.map((chip) => chip.label).join(', ')}`}
                >
                    <span className={clsx(styles.Chip, styles.ChipOverflow)}>+{hidden.length}</span>
                </Tooltip>
            )}
        </div>
    );
}

interface FinancialsFromBankingV2Props {
    /** Applied Banking-tab Time Period window ('' when unset); scopes both the table and drawers. */
    startDate?: string;
    endDate?: string;
    /** Reports the org's full bank_transactions availability (span.minDate/maxDate,
     *  'YYYY-MM-DD') so the Banking tab widens its date-picker envelope to it. */
    onDataSpan?: (minDate: string, maxDate: string) => void;
    /** Reports the API-applied default window (startDate/endDate, 'YYYY-MM-DD') so the
     *  CFB time-period pickers can display it when no explicit selection exists. */
    onAppliedWindow?: (startDate: string, endDate: string) => void;
    /** Divisor to apply to all table amount cells (1 = raw ₹, 1e5 = Lakhs, 1e7 = Crore). Drawer unaffected. */
    unitDivisor?: number;
    /** Edit mode is owned by BankDataTable and passed down. */
    editMode?: boolean;
    onExitEdit?: () => void;
    /** Selected account IDs for the CFB multi-select filter; passed to the buckets/drawer requests. */
    selectedAccountIds?: string[];
    /** Selected account numbers for the CFB multi-select filter; narrows the totals client-side. */
    selectedAccountNumbers?: string[];
}

function FinancialsFromBankingV2({
    startDate = '',
    endDate = '',
    onDataSpan,
    onAppliedWindow,
    unitDivisor = 1,
    editMode = false,
    onExitEdit,
    selectedAccountIds,
    selectedAccountNumbers,
}: FinancialsFromBankingV2Props) {
    const [tableData, setTableData] = useState<any>();
    const [aggregates, setAggregates] = useState<CashflowAggregates>(EMPTY_AGGREGATES);
    const [fetchingData, setFetchingData] = useState(false);
    const [rationaleEntities, setRationaleEntities] = useState<EntityRationale[]>([]);
    const [drawer, setDrawer] = useState<DrawerState | null>(null);
    const drawerRef = useRef<DrawerState | null>(null);
    useEffect(() => {
        drawerRef.current = drawer;
    }, [drawer]);
    const [effectiveWindow, setEffectiveWindow] = useState<{ startDate: string; endDate: string } | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    // Per-group visible leaf count; absent = default page size. Each "Load more" click adds one page.
    const [visibleCountByGroup, setVisibleCountByGroup] = useState<Map<string, number>>(new Map());

    const [stagedEdits, setStagedEdits] = useState<Map<number, StagedEdit>>(new Map());
    const [saving, setSaving] = useState(false);
    const [orgEntities, setOrgEntities] = useState<OrgEntity[]>([]);

    const params = useParams();
    const orgId = params.id as string;

    const { config, isConfigLoaded } = useVisibilityConfig();
    const canEditDrawer =
        getCashflowEditLevel(config, isConfigLoaded, VISIBILITY_KEYS.CASHFLOW_EDIT.ENABLED, process.env.REACT_APP_ENVIRONMENT) >=
        VISIBILITY_LEVEL.WRITE;

    const { startRequest, isCurrent } = useLatestRequestGuard();
    const { startRequest: startAggregatesRequest, isCurrent: isAggregatesCurrent } = useLatestRequestGuard();
    const { startRequest: startEntitiesRequest, isCurrent: isEntitiesCurrent } = useLatestRequestGuard();
    const isMounted = useRef(false);

    const noData = !tableData?.headers?.length;

    const periodHeaders = useMemo(() => tableData?.headers ?? [], [tableData]);
    const layout = useMemo(() => buildCashflowLayout(tableData?.rows ?? []), [tableData?.rows]);
    // +1 Classification column in edit mode (between Tagging Rational and first FY column).
    const totalColumnCount = editMode ? periodHeaders.length + 4 : periodHeaders.length + 3;

    // Single readiness flag: false when any EntityRationale lacks classificationId (pre-contract backend).
    // Used by both the chip map (grain-keyed suppression) and the rationale label map (kind-2 fallback).
    const grainReady = rationaleEntities.every((e) => e.classificationId !== undefined);

    // Chip map keyed by grainChipKey(entityId, classificationId); manual grains self-exclude via
    // chipInfoForEntity returning null. When grainReady is false (pre-contract backend), falls back
    // to entity-keyed lookup so old-backend chips continue to resolve.
    const entityChipMap = useMemo<Map<string, ChipInfo>>(() => {
        const map = new Map<string, ChipInfo>();
        for (const entity of rationaleEntities) {
            const gk = grainChipKey(entity.entityId, entity.classificationId);
            const info = chipInfoForEntity(entity);
            const key = grainReady ? gk : String(entity.entityId);
            // First-wins on duplicate keys (two non-manual chip-bearing siblings at the same
            // key), matching entityManualRationaleMap / entityEditRationaleMap, so the displayed
            // chip and the edit pre-fill come from the same source row (deterministic, not
            // backend-order-dependent).
            if (info && !map.has(key)) map.set(key, info);
        }
        return map;
    }, [rationaleEntities, grainReady]);

    // Browse-mode plain-pill for purely-manual entity grains (rationaleIsManual=true with no
    // non-manual sibling at the same grain key). When a non-manual residue grain shares the key,
    // the entity row represents the residue bucket — no text borrowed from the manual grain.
    // Leak-freeness rests on the "residue ⇒ non-manual" invariant from getBankEntityRationaleQuery:
    // if a residue grain is ever marked rationaleIsManual=true, a kind-2 row could re-borrow the label.
    const entityManualRationaleMap = useMemo<Map<string, string>>(() => {
        const manualLabels = new Map<string, string>();
        const hasNonManual = new Set<string>();
        for (const entity of rationaleEntities) {
            const gk = grainChipKey(entity.entityId, entity.classificationId);
            if (entity.rationaleIsManual && entity.rationale) {
                if (!manualLabels.has(gk)) manualLabels.set(gk, toDisplayName(entity.rationale));
            } else {
                hasNonManual.add(gk);
            }
        }
        for (const gk of hasNonManual) manualLabels.delete(gk);
        return manualLabels;
    }, [rationaleEntities]);

    // Edit-mode pre-fill for kind-2 entity rows: the served rationale string (non-manual, non-noise)
    // keyed by grainChipKey so it is grain-scoped and cannot borrow from a manual sibling grain.
    const entityEditRationaleMap = useMemo<Map<string, string>>(() => {
        if (!editMode) return new Map();
        const map = new Map<string, string>();
        for (const entity of rationaleEntities) {
            if (entity.rationaleIsManual) continue;
            const r = entity.rationale;
            if (!r || isNoiseRationale(r)) continue;
            const gk = grainChipKey(entity.entityId, entity.classificationId);
            if (!map.has(gk)) map.set(gk, toDisplayName(r));
        }
        return map;
    }, [rationaleEntities, editMode]);

    // Clear staged edits and exit edit mode when the org changes so org A's edits
    // cannot be saved under org B's investeeOrgId.
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        setStagedEdits(new Map());
        onExitEdit?.();
    }, [orgId]);

    // Table refetches on org change, on the Banking tab's applied Time Period, and on
    // account filter changes. Both arrays are useMemo-stable in the parent so their
    // references change only when the selection changes — no stringify needed.
    useEffect(() => {
        fetchTableData();
    }, [orgId, startDate, endDate, selectedAccountIds]);

    useEffect(() => {
        fetchRationaleData();
    }, [orgId]);

    // Deps use content keys so identity changes on bankingOverview refresh (same selection,
    // new array reference) do not trigger spurious refetches.
    const hasSelectedAccountIds = !!selectedAccountIds?.length;
    useEffect(() => {
        fetchAggregates();
    }, [orgId, effectiveWindow?.startDate, effectiveWindow?.endDate, selectedAccountNumbers?.join('|'), hasSelectedAccountIds]);

    useEffect(() => {
        if (!editMode && !canEditDrawer) {
            setOrgEntities([]);
            return;
        }
        const requestToken = startEntitiesRequest();
        fetchOrgEntitiesService(orgId)
            .then((res) => {
                if (!isEntitiesCurrent(requestToken)) return;
                const raw = res.data?.responseBody?.data;
                const entities: OrgEntity[] = Array.isArray(raw) ? raw : [];
                setOrgEntities(entities);
            })
            .catch(() => {
                // Graceful degradation — autocomplete just won't populate; rename still works.
            });
    }, [editMode, canEditDrawer, orgId]);

    const fetchTableData = async (silent = false) => {
        if (!silent) setFetchingData(true);
        const requestToken = startRequest();
        try {
            const response = await fetchBankStatementCashflowBucketsService({
                startDate,
                endDate,
                ...(selectedAccountIds?.length ? { accountIds: selectedAccountIds } : {}),
            });
            if (!isCurrent(requestToken)) return;
            const responseData = response.data?.responseData;
            if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
                const data = response.data?.data ?? {};
                const transformedRows = data?.rows?.map((row: any) => ({
                    ...row,
                    amountsMap: amountsToMap(row?.amounts),
                    subRows: row?.subRows?.map((subrow: any) => ({
                        ...subrow,
                        amountsMap: amountsToMap(subrow?.amounts),
                    })),
                }));
                setTableData({
                    headers: data?.headers ?? [],
                    rows: transformedRows,
                });
                // span always reflects full availability, independent of any window.
                if (data?.span?.minDate && data?.span?.maxDate) {
                    onDataSpan?.(data.span.minDate, data.span.maxDate);
                }
                // The server applies a last-18-months-of-data default when unwindowed;
                // drawers must use the same effective window for table↔drawer parity.
                const aw = data?.appliedWindow?.startDate && data?.appliedWindow?.endDate ? data.appliedWindow : null;
                setEffectiveWindow(aw ? { startDate: aw.startDate, endDate: aw.endDate } : null);
                if (aw) onAppliedWindow?.(aw.startDate, aw.endDate);
            }
            setFetchingData(false);
        } catch {
            if (isCurrent(requestToken)) setFetchingData(false);
        }
    };

    // The bucket response carries no Opening/Net Cashflow/Closing rows — pull them from the
    // account-summary source and sum across accounts. Pass the effective window so aggregates
    // stay in sync with the table period; no selectedBankId (unknown to BankStatementSummaryRequest).
    const fetchAggregates = async () => {
        const requestToken = startAggregatesRequest();
        await fetchBankStatementData(effectiveWindow ? { startDate: effectiveWindow.startDate, endDate: effectiveWindow.endDate } : {}, {
            onSuccess: (bankData) => {
                if (!isAggregatesCurrent(requestToken)) return;
                setAggregates(buildCashflowAggregates(bankData?.summaryData ?? [], selectedAccountNumbers, hasSelectedAccountIds));
            },
            onError: () => {
                if (!isAggregatesCurrent(requestToken)) return;
                setAggregates(EMPTY_AGGREGATES);
            },
        });
    };

    const fetchRationaleData = async () => {
        try {
            const response = await fetchBankEntityRationaleService();
            const responseData = response.data?.responseData;
            if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
                const entities: EntityRationale[] = response.data?.data?.entities ?? [];
                setRationaleEntities(entities);
            }
        } catch {
            // Degrade gracefully — no chips, base table unaffected
        }
    };

    // Deduplicated list of non-noise rationale strings for the rationale dropdown.
    const rationaleOptions = useMemo<string[]>(() => {
        const seen = new Set<string>();
        const result: string[] = [];
        for (const e of rationaleEntities) {
            if (e.rationale && !isNoiseRationale(e.rationale) && !seen.has(e.rationale)) {
                seen.add(e.rationale);
                result.push(e.rationale);
            }
        }
        return result;
    }, [rationaleEntities]);

    const handleStage = (entityId: number, patch: Partial<StagedEdit>) => {
        setStagedEdits((prev) => {
            const next = new Map(prev);
            const existing = next.get(entityId) ?? { currentClassificationId: patch.currentClassificationId ?? 0 };
            const clsId = patch.currentClassificationId ?? 0;
            const key = grainKey(clsId, patch.currentRationale);
            const grains = new Map(existing.grainEdits ?? []);
            const g = grains.get(key) ?? { currentClassificationId: clsId, currentRationale: patch.currentRationale ?? null };

            if (patch.rationale !== undefined) {
                grains.set(key, { ...g, rationale: patch.rationale, clearRationale: undefined });
            } else if (patch.clearRationale) {
                // clearRationale co-exists with a staged re-link (targetEntityId) on the same grain.
                grains.set(key, { ...g, rationale: undefined, clearRationale: true });
            } else if (patch.unlink) {
                // Unlink clears all other ops for this grain.
                grains.set(key, { currentClassificationId: clsId, currentRationale: patch.currentRationale ?? null, unlink: true });
            } else if (patch.targetEntityId !== undefined) {
                // Re-link co-exists with clearRationale; still strips the truly exclusive fields.
                const { entityName: _e, unlink: _u, rationale: _r, classificationId: _cl, clearClassification: _cc, ...rest } = g;
                grains.set(key, { ...rest, targetEntityId: patch.targetEntityId, targetEntityName: patch.targetEntityName });
            } else if (patch.entityName !== undefined) {
                // Rename clears staged re-link and unlink within this grain (round-15 fix preserved).
                const { targetEntityId: _t, targetEntityName: _tn, unlink: _u, ...rest } = g;
                grains.set(key, { ...rest, entityName: patch.entityName });
            } else if (patch.clearClassification) {
                const { classificationId: _cl, ...rest } = g;
                grains.set(key, { ...rest, clearClassification: true });
            } else if (patch.classificationId !== undefined) {
                const { clearClassification: _cc, ...rest } = g;
                grains.set(key, { ...rest, classificationId: patch.classificationId });
            }
            next.set(entityId, { ...existing, grainEdits: grains });
            return next;
        });
    };

    const handleRestore = (entityId: number, rowGrainKey: string) => {
        setStagedEdits((prev) => {
            const entry = prev.get(entityId);
            if (!entry) return prev;
            const g = entry.grainEdits?.get(rowGrainKey);
            if (!g) return prev;
            const grains = new Map(entry.grainEdits);
            // Strip entity ops from this grain; keep any rationale/classification ops.
            const { unlink: _u, targetEntityId: _t, targetEntityName: _tn, entityName: _en, ...rest } = g;
            const hasOps =
                rest.rationale !== undefined || rest.clearRationale || rest.classificationId !== undefined || rest.clearClassification;
            if (hasOps) {
                grains.set(rowGrainKey, rest as typeof g);
            } else {
                grains.delete(rowGrainKey);
            }
            const next = new Map(prev);
            if (grains.size === 0) {
                next.delete(entityId);
            } else {
                next.set(entityId, { ...entry, grainEdits: grains });
            }
            return next;
        });
    };

    const handleCancel = () => {
        setStagedEdits(new Map());
        onExitEdit?.();
    };

    // One pending count per staged grain (every op type lives in grainEdits after D30).
    let pendingCount = 0;
    for (const edit of stagedEdits.values()) pendingCount += edit.grainEdits?.size ?? 0;

    /** currentClassificationId filler for the txn-scoped drawer save path, which targets
     *  individual transactions rather than an entity grain. */
    const TXN_SCOPED_NO_CURRENT_CLASSIFICATION = 0;

    /** Refetch table + rationale data and reopen the drawer if one was open. Called after any successful save. */
    const onSaveSuccess = () => {
        fetchTableData(true);
        fetchRationaleData();
        if (drawerRef.current) {
            openDrawer(
                drawerRef.current.entityName,
                { ...drawerRef.current.payload, ...(effectiveWindow ?? {}) },
                drawerRef.current.entityId,
            );
        }
    };

    /** Call the edit API and assert success; throws with the server message on API failure,
     *  or the generic fallback on a network/service error. */
    const submitEdits = async (edits: EditClassificationEdit[]): Promise<void> => {
        let response;
        try {
            response = await editClassificationsService(orgId, edits);
        } catch {
            throw new Error('Save failed — please retry.');
        }
        const responseData = response.data?.responseData;
        const responseBody = response.data?.responseBody;
        if (responseData?.responseCode !== JAVA_API_STATUS.SUCCESS || !responseBody?.success) {
            throw new Error(responseBody?.message ?? 'Save failed — please retry.');
        }
    };

    const handleSave = async () => {
        if (pendingCount === 0) return;
        setSaving(true);
        const edits: EditClassificationEdit[] = [];
        for (const [entityId, edit] of stagedEdits) {
            if (!edit.grainEdits) continue;
            for (const grain of edit.grainEdits.values()) {
                edits.push({
                    entityId,
                    currentClassificationId: grain.currentClassificationId ?? edit.currentClassificationId,
                    currentRationale: grain.currentRationale,
                    ...(grain.rationale !== undefined && { rationale: grain.rationale }),
                    ...(grain.clearRationale && { clearRationale: true }),
                    ...(grain.classificationId !== undefined && { classificationId: grain.classificationId }),
                    ...(grain.clearClassification && { clearClassification: true }),
                    ...(grain.unlink && { unlink: true }),
                    ...(grain.targetEntityId !== undefined && { targetEntityId: grain.targetEntityId }),
                    ...(grain.entityName !== undefined && { entityName: grain.entityName }),
                });
            }
        }
        try {
            await submitEdits(edits);
            setStagedEdits(new Map());
            onExitEdit?.();
            onSaveSuccess();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed — please retry.');
        } finally {
            setSaving(false);
        }
    };

    const handleDrawerSave = async (entityId: number | null, transactionIds: number[], edit: DrawerBulkEdit) => {
        const editItem: EditClassificationEdit = {
            entityId,
            currentClassificationId: TXN_SCOPED_NO_CURRENT_CLASSIFICATION,
            transactionIds,
            ...(edit.classificationId !== undefined && { classificationId: edit.classificationId }),
            ...(edit.rationale !== undefined && { rationale: edit.rationale }),
            ...(edit.targetEntityId !== undefined && { targetEntityId: edit.targetEntityId }),
            ...(edit.createEntity && { createEntity: true, entityName: edit.newEntityName }),
        };
        try {
            await submitEdits([editItem]);
            onSaveSuccess();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed — please retry.');
            throw e;
        }
    };

    const openDrawer = async (displayName: string, payload: BucketPayload, entityId?: number) => {
        const base = { entityName: displayName, entityId, payload };
        setDrawer({ ...base, transactions: [], loading: true });
        try {
            const response = await fetchBankTransactionsByBucketService(payload);
            const responseData = response.data?.responseData;
            const rows: BankTransaction[] = responseData?.responseCode === JAVA_API_STATUS.SUCCESS ? response.data?.data ?? [] : [];
            setDrawer({ ...base, transactions: rows, loading: false });
        } catch {
            setDrawer({ ...base, transactions: [], loading: false });
        }
    };

    const viewTransactions = (displayName: string, payload: BucketPayload, entityId?: number) => {
        Mixpanel.track(AICA_CASHFLOW_BANKING_TRANSACTIONS_VIEWED, { rowName: displayName });
        openDrawer(displayName, payload, entityId);
    };

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    // Shared <colgroup> applied to every table in the multi-table stack, keyed off the same
    // widths as the SCSS cell min/max-width values. Edit mode adds one fixed-width Classification
    // col (14rem) between Tagging Rational and the first period column.
    const renderColGroup = () => (
        <colgroup>
            <col style={{ width: `${NAME_COL_WIDTH}px` }} />
            <col style={{ width: `${colWidths.rationale}px` }} />
            {editMode && <col style={{ width: `${CLASSIFICATION_COL_WIDTH}px` }} />}
            {periodHeaders.map((h: string, i: number) => (
                <col key={h} style={{ width: `${colWidths.periods[i] ?? PERIOD_MIN}px` }} />
            ))}
            <col style={{ width: `${colWidths.total}px` }} />
        </colgroup>
    );

    // Measured container width, so leftover space is handed to the month columns instead of
    // sitting as dead space right of Total.
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        const read = () => setContainerWidth(el.clientWidth);
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /** Widths measured from what will actually render; recomputed on data/period/unitDivisor change. */
    const colWidths = useMemo(() => {
        const rows: any[] = tableData?.rows ?? [];

        // Every value that can appear in a period column, including the derived Net/Opening/Closing rows.
        const periodWidth = (header: string): number => {
            let widest = measureText(header, HEAD_FONT);
            const consider = (raw: any) => {
                if (raw == null) return;
                const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
                const text = Number.isFinite(n) ? formatScaledAmount(n, unitDivisor) : String(raw);
                widest = Math.max(widest, measureText(text, CELL_FONT));
            };
            let columnSum = 0;
            rows.forEach((row) => {
                consider(row?.amountsMap?.[header]);
                const v = row?.amountsMap?.[header];
                const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
                if (Number.isFinite(n)) columnSum += n;
                (row?.subRows ?? []).forEach((sub: any) => consider(sub?.amountsMap?.[header]));
            });
            consider(columnSum); // upper bound for a section Net row
            consider(aggregates.opening?.[header]);
            consider(aggregates.netCashflow?.[header]);
            consider(aggregates.closing?.[header]);
            return clamp(widest + CELL_PADDING + WIDTH_BUFFER, PERIOD_MIN, PERIOD_MAX);
        };

        const totalWidth = (() => {
            let widest = measureText('Total', HEAD_FONT);
            let grand = 0;
            rows.forEach((row) => {
                const t = rowTotal(row?.amountsMap);
                grand += t;
                widest = Math.max(widest, measureText(formatScaledAmount(t, unitDivisor), CELL_FONT));
                (row?.subRows ?? []).forEach((sub: any) => {
                    widest = Math.max(widest, measureText(formatScaledAmount(rowTotal(sub?.amountsMap), unitDivisor), CELL_FONT));
                });
            });
            widest = Math.max(widest, measureText(formatScaledAmount(grand, unitDivisor), CELL_FONT));
            return clamp(widest + CELL_PADDING + WIDTH_BUFFER, TOTAL_MIN, TOTAL_MAX);
        })();

        // Chips are small text but the column header is 14px — usually the header wins.
        const rationaleWidth = (() => {
            let widest = measureText('Tagging Rational', HEAD_FONT);
            rationaleEntities.forEach((e) => {
                const label = e?.rationale ?? e?.entityRationale ?? '';
                // chip = label + 16px padding + 4px gap + 10px info icon
                if (label) widest = Math.max(widest, measureText(String(label), CHIP_FONT) + 30);
            });
            const base = clamp(widest + CELL_PADDING + WIDTH_BUFFER, RATIONALE_MIN, RATIONALE_MAX);
            // Edit mode: the rationale column holds a fixed-width "Select Rational" pill (kept
            // uniform with the entity/classification pills), so widen it to the Classification
            // column width — a measured ~160px max can't hold the uniform pill.
            return editMode ? Math.max(base, CLASSIFICATION_COL_WIDTH) : base;
        })();

        const periods: number[] = periodHeaders.map((h: string) => periodWidth(h));

        // Measured widths are the floor: any leftover container space goes to the month
        // columns (not min-width:100%, which would redistribute across every column instead).
        // The table's own width is set to the final column sum, so fixed layout stays authoritative.
        // Edit mode inserts a fixed-width Classification col, so count it against the surplus.
        const declared =
            NAME_COL_WIDTH + rationaleWidth + totalWidth + periods.reduce((a, b) => a + b, 0) + (editMode ? CLASSIFICATION_COL_WIDTH : 0);
        const surplus = containerWidth - declared;
        if (surplus > 0 && periods.length > 0) {
            const each = Math.floor(surplus / periods.length);
            for (let i = 0; i < periods.length; i++) periods[i] += each;
            // Give the rounding remainder to the last column so the table lands flush.
            periods[periods.length - 1] += surplus - each * periods.length;
        }

        const tableWidth =
            NAME_COL_WIDTH + rationaleWidth + (editMode ? CLASSIFICATION_COL_WIDTH : 0) + periods.reduce((a, b) => a + b, 0) + totalWidth;

        return { rationale: rationaleWidth, total: totalWidth, periods, tableWidth };
    }, [tableData, aggregates, periodHeaders, unitDivisor, rationaleEntities, containerWidth, editMode]);

    // Wire format: comma-formatted strings with a leading '-' for negatives (e.g. '-40,000').
    // Parenthesized negatives never arrive from the API — display-only convention.
    const scaledFmt = (raw: any): string => {
        if (raw == null) return '-';
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
        return Number.isFinite(n) ? formatScaledAmount(n, unitDivisor) : String(raw);
    };

    const periodCells = (amountsMap: Record<string, any> = {}) =>
        periodHeaders.map((header: string, j: number) => {
            const raw = amountsMap?.[header];
            const isNeg = raw != null && (typeof raw === 'number' ? raw < 0 : String(raw).trim().startsWith('-'));
            return (
                <td key={`${header}-${j}`} className={clsx(styles.TableBodyRowItem, { [styles.Negative]: isNeg })}>
                    {scaledFmt(raw)}
                </td>
            );
        });

    const totalCell = (amountsMap: Record<string, any> = {}) => {
        const total = rowTotal(amountsMap);
        return (
            <td className={clsx(styles.TableBodyRowItem, styles.TotalCell, { [styles.Negative]: total < 0 })}>
                {formatScaledAmount(total, unitDivisor)}
            </td>
        );
    };

    const stickyCol2Style = editMode ? { left: NAME_COL_WIDTH } : undefined;
    const stickyCol3Style = { left: NAME_COL_WIDTH + colWidths.rationale };
    const tableStyle = { width: `${colWidths.tableWidth}px` };

    const renderSectionBand = (band: string) => (
        <tr key={band} className={styles.SectionBandRow}>
            <td className={styles.SectionBandCell} colSpan={totalColumnCount}>
                <span className={styles.SectionBandLabel}>{band}</span>
            </td>
        </tr>
    );

    const renderNetRow = (label: string, amountsMap: Record<string, number>) => (
        <tr key={label} className={styles.NetRow}>
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell)}>{label}</td>
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell, { [styles.StickyCol2]: editMode })} style={stickyCol2Style} />
            {editMode && <td className={clsx(styles.TableBodyRowItem, styles.NetCell, styles.StickyCol3)} style={stickyCol3Style} />}
            {periodHeaders.map((header: string, j: number) => {
                const v = amountsMap?.[header];
                return (
                    <td
                        key={`${header}-${j}`}
                        className={clsx(styles.TableBodyRowItem, styles.NetCell, { [styles.Negative]: v != null && v < 0 })}
                    >
                        {v != null ? formatScaledAmount(v, unitDivisor) : '-'}
                    </td>
                );
            })}
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell, { [styles.Negative]: rowTotal(amountsMap) < 0 })}>
                {formatScaledAmount(rowTotal(amountsMap), unitDivisor)}
            </td>
        </tr>
    );

    const renderAggregateRow = (
        label: string,
        amountsMap: Record<string, number>,
        showFormula = false,
        isNet = false,
        totalMode: 'sum' | 'first' | 'last' = 'sum',
    ) => {
        const firstPeriod = periodHeaders[0];
        const lastPeriod = periodHeaders[periodHeaders.length - 1];
        const aggTotal =
            totalMode === 'first'
                ? amountsMap?.[firstPeriod] ?? 0
                : totalMode === 'last'
                ? amountsMap?.[lastPeriod] ?? 0
                : rowTotal(amountsMap);
        return (
            <tr key={label} className={clsx(styles.TableBodyRow, isNet ? styles.AggregateNetRow : styles.AggregateRow)}>
                <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell)}>
                    {showFormula ? (
                        <Tooltip content={FORMULA_HOVER} direction="top" fitContent>
                            <span>{label}</span>
                        </Tooltip>
                    ) : (
                        label
                    )}
                </td>
                <td
                    className={clsx(styles.TableBodyRowItem, styles.AggregateCell, { [styles.StickyCol2]: editMode })}
                    style={stickyCol2Style}
                />
                {editMode && (
                    <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell, styles.StickyCol3)} style={stickyCol3Style} />
                )}
                {periodHeaders.map((header: string, j: number) => {
                    const v = amountsMap?.[header];
                    return (
                        <td
                            key={`${header}-${j}`}
                            className={clsx(styles.TableBodyRowItem, styles.AggregateCell, { [styles.Negative]: v != null && v < 0 })}
                        >
                            {v != null ? formatScaledAmount(v, unitDivisor) : '-'}
                        </td>
                    );
                })}
                <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell, { [styles.Negative]: aggTotal < 0 })}>
                    {formatScaledAmount(aggTotal, unitDivisor)}
                </td>
            </tr>
        );
    };

    const renderEntitySubRow = (subRow: CashflowSubRow, groupKey: string, groupLabel: string) => {
        const isOther = subRow.bucketType === 'other';
        const isEntity = subRow.bucketType === 'entity';

        // displayName: raw string used for keys and drawer title; formattedName: title-cased for render only.
        const displayName = isOther ? `Other ${groupLabel}` : subRow.name ?? '';
        const formattedName = isOther ? displayName : toDisplayName(displayName);

        // Chip ladder: tier-1 by grain → source → fallback name pill; tier-2 by grain; tier-3 none.
        // Manual grains return null from chipInfoForEntity so they never enter entityChipMap.
        let chipInfo: ChipInfo | null = null;
        if (!isOther) {
            if (subRow.entityId != null) {
                const chipKey = grainReady ? grainChipKey(subRow.entityId, subRow.classificationId) : String(subRow.entityId);
                chipInfo = entityChipMap.get(chipKey) ?? null;
            }
            if (chipInfo == null && !isEntity) {
                chipInfo = getSourceChipInfo(subRow.source);
            }
        }
        // Parsed once; used by both the grey pill (fallbackLabel) and the grain key (currentRationale).
        const parsedRationale =
            subRow.bucketType === 'rationale' && subRow.bucketKey != null ? parseRationaleFromBucketKey(subRow.bucketKey) : null;

        // Shared grain key for entity-row map lookups below (same expression as chipKey above).
        const entityGrainKey = isEntity
            ? grainReady
                ? grainChipKey(subRow.entityId!, subRow.classificationId)
                : String(subRow.entityId!)
            : null;

        // Fallback rung: no structured chip → grey pill from served rationale.
        // kind-1 (rationale): rationale parsed from composite bucketKey, payment-method noise skipped.
        // kind-2 (entity): manual grains show the user-entered text; non-manual residue rows show nothing.
        // kind-3 (other) and rows with a structured chip: excluded.
        const fallbackLabel = (() => {
            if (isOther || chipInfo != null) return null;
            if (isEntity) return entityGrainKey ? entityManualRationaleMap.get(entityGrainKey) ?? null : null;
            return parsedRationale && !isNoiseRationale(parsedRationale) ? toDisplayName(parsedRationale) : null;
        })();

        const chips = rationaleChips(!!subRow.edited, chipInfo, fallbackLabel);

        // In edit mode: chip-bearing rows use chip label; kind-2 entity rows use grain-scoped
        // served rationale from entityEditRationaleMap; kind-1 and other rows use fallbackLabel.
        const editRationale = (() => {
            if (!editMode) return fallbackLabel;
            if (chipInfo != null) return chipInfo.label;
            if (isEntity) return entityGrainKey ? entityEditRationaleMap.get(entityGrainKey) ?? null : null;
            return fallbackLabel;
        })();

        // Drawer payload per bucket type.
        const drawerPayload: BucketPayload = {
            bucketType: subRow.bucketType,
            bucketKey: subRow.bucketKey ?? null,
            classificationId: subRow.classificationId,
            ...(effectiveWindow ? effectiveWindow : {}),
            ...(selectedAccountIds?.length ? { accountIds: selectedAccountIds } : {}),
        };

        const viewLink = (label: string) => (
            <span
                className={styles.ViewTxn}
                role="button"
                tabIndex={0}
                onClick={() => viewTransactions(formattedName, drawerPayload, subRow.entityId)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        viewTransactions(formattedName, drawerPayload, subRow.entityId);
                    }
                }}
            >
                {label}
                <ViewTxnArrowIcon />
            </span>
        );

        const hasEntityId = subRow.entityId != null;
        const staged = hasEntityId ? stagedEdits.get(subRow.entityId!) : undefined;
        // kind-1 grain = parsed rationale; kind-2 (residue) = null.
        const currentRationale = parsedRationale;
        const rowGrainKey = grainKey(subRow.classificationId ?? 0, currentRationale);
        const grainStaged = staged?.grainEdits?.get(rowGrainKey);

        return (
            <tr
                key={`${groupKey}-${subRow.bucketKey ?? subRow.name}-${subRow.bucketType}`}
                className={clsx(styles.TableBodyRow, styles.SubRow)}
            >
                {/* Col 1: bubble (edit mode, entity rows) or plain name (browse/non-entity) */}
                <td className={styles.TableBodyRowItem}>
                    {editMode && hasEntityId ? (
                        <span className={clsx(styles.NameCell, styles.NameCellEdit)}>
                            <EntityBubbleCell
                                entityId={subRow.entityId!}
                                entityName={formattedName}
                                currentClassificationId={subRow.classificationId ?? 0}
                                rowCurrentRationale={currentRationale}
                                rowGrainKey={rowGrainKey}
                                grainStaged={grainStaged}
                                onStage={handleStage}
                                onRestore={handleRestore}
                                orgEntities={orgEntities}
                            />
                            {viewLink(VIEW_TXN_LABEL_EDIT)}
                        </span>
                    ) : (
                        <span className={styles.NameCell}>
                            <LeafName text={formattedName || '-'} className={clsx(styles.NameText, { [styles.ResidualName]: isOther })} />
                            {viewLink(editMode ? VIEW_TXN_LABEL_EDIT : VIEW_TXN_LABEL)}
                        </span>
                    )}
                </td>
                {/* Col 2: Tagging Rational — chips (browse) or rationale dropdown (edit) */}
                <td className={clsx(styles.TableBodyRowItem, { [styles.StickyCol2]: editMode })} style={stickyCol2Style}>
                    {editMode && hasEntityId ? (
                        <RationaleCell
                            entityId={subRow.entityId!}
                            currentClassificationId={subRow.classificationId ?? 0}
                            currentRationale={editRationale}
                            rowCurrentRationale={currentRationale}
                            grainStaged={grainStaged}
                            rationaleOptions={rationaleOptions}
                            onStage={handleStage}
                        />
                    ) : (
                        chips.length > 0 && (
                            <RationaleChipStrip
                                key={chips.map((c) => `${c.kind}:${c.label}`).join('|')}
                                chips={chips}
                                chipInfo={chipInfo}
                            />
                        )
                    )}
                </td>
                {/* Col 3: Classification — edit mode only; empty for rows without entityId */}
                {editMode && (
                    <td className={clsx(styles.TableBodyRowItem, styles.StickyCol3)} style={stickyCol3Style}>
                        {hasEntityId && (
                            <ClassificationCell
                                entityId={subRow.entityId!}
                                currentClassificationId={subRow.classificationId ?? 0}
                                rowCurrentRationale={currentRationale}
                                grainStaged={grainStaged}
                                onStage={handleStage}
                            />
                        )}
                    </td>
                )}
                {periodCells(subRow.amountsMap)}
                {totalCell(subRow.amountsMap)}
            </tr>
        );
    };

    const renderGroupRow = (groupRow: GroupRow, inSection = false) => {
        const { row, count, label, kind } = groupRow;
        const groupKey = `${kind}:${label}`;
        const hasSubRows = count > 0;
        const expanded = hasSubRows && expandedGroups.has(groupKey);
        const toggle = () => hasSubRows && toggleGroup(groupKey);

        let leafRows: JSX.Element[] = [];
        if (expanded) {
            const sorted = sortedSubRows(row.subRows ?? []) as CashflowSubRow[];
            const nonOther = sorted.filter((s) => s.bucketType !== 'other');
            const other = sorted.filter((s) => s.bucketType === 'other');
            const visibleCount = visibleCountByGroup.get(groupKey) ?? LEAF_PAGE_SIZE;
            const visibleLeaves = nonOther.slice(0, visibleCount);
            const remaining = nonOther.length - visibleCount;
            const loadMore = () => setVisibleCountByGroup((prev) => new Map(prev).set(groupKey, visibleCount + LEAF_PAGE_SIZE));
            leafRows = [
                ...visibleLeaves.map((s) => renderEntitySubRow(s, groupKey, label)),
                ...(remaining > 0
                    ? [
                          <tr key={`${groupKey}-load-more`}>
                              <td className={styles.TableBodyRowItem}>
                                  <span
                                      className={styles.LoadMore}
                                      role="button"
                                      tabIndex={0}
                                      onClick={loadMore}
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              loadMore();
                                          }
                                      }}
                                  >
                                      <img src={caretRightPurple} alt="" className={styles.LoadMoreChevron} />
                                      {`See more (${remaining} Transactions)`}
                                  </span>
                              </td>
                              <td className={clsx(styles.TableBodyRowItem, { [styles.StickyCol2]: editMode })} style={stickyCol2Style} />
                              {editMode && <td className={clsx(styles.TableBodyRowItem, styles.StickyCol3)} style={stickyCol3Style} />}
                              {periodHeaders.map((header: string, j: number) => (
                                  <td key={`${groupKey}-load-more-${header}-${j}`} className={styles.TableBodyRowItem} />
                              ))}
                              <td className={clsx(styles.TableBodyRowItem, styles.TotalCell)} />
                          </tr>,
                      ]
                    : []),
                ...other.map((s) => renderEntitySubRow(s, groupKey, label)),
            ];
        }

        return (
            <Fragment key={groupKey}>
                <tr className={inSection ? clsx(styles.GroupRow, styles.SectionGroupRow) : styles.GroupRow}>
                    <td className={styles.TableBodyRowItem}>
                        <span
                            className={clsx(styles.GroupNameCell, { [styles.GroupNameDisabled]: !hasSubRows })}
                            role="button"
                            tabIndex={hasSubRows ? 0 : -1}
                            onClick={toggle}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggle();
                                }
                            }}
                        >
                            <img
                                src={dropdownBlack}
                                alt=""
                                width="10px"
                                className={styles.Chevron}
                                style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                            />
                            <span className={clsx(SPECIAL_LABEL_CLASS[kind])}>{label}</span>
                            <span className={styles.CountBadge}>{count}</span>
                        </span>
                    </td>
                    <td className={clsx(styles.TableBodyRowItem, { [styles.StickyCol2]: editMode })} style={stickyCol2Style} />
                    {editMode && <td className={clsx(styles.TableBodyRowItem, styles.StickyCol3)} style={stickyCol3Style} />}
                    {periodCells(row.amountsMap)}
                    {totalCell(row.amountsMap)}
                </tr>
                {leafRows}
            </Fragment>
        );
    };

    return (
        <div className={clsx(styles.FinancialsFromBankingV2, { [styles.EditModeActive]: editMode })}>
            <div className={styles.TableContainer} ref={tableContainerRef}>
                {fetchingData ? (
                    <LoadingSpinner height="60px" style={{ marginTop: '100px' }} color="black" />
                ) : noData ? (
                    <EmptyBank text="Data Not Available" />
                ) : (
                    // One <table> per section, so sticky offsets reset per section (band at
                    // 2.25rem, group rows at 4.5rem) as each table scrolls out.
                    <>
                        {/* A sticky <div> (not the <table> itself) — a sticky <table> creates a
                            stacking context in Chrome that swallows clicks on rows below it. */}
                        <div className={styles.StickyHeader}>
                            <table className={styles.Table} style={tableStyle}>
                                {renderColGroup()}
                                <thead className={styles.TableHead}>
                                    <tr className={styles.TableHeadRow}>
                                        <th className={styles.TableHeadRowItem} />
                                        <th
                                            className={clsx(styles.TableHeadRowItem, styles.RationaleHeader, {
                                                [styles.StickyRationaleHeader]: editMode,
                                            })}
                                            style={stickyCol2Style}
                                        >
                                            Tagging Rational
                                        </th>
                                        {editMode && (
                                            <th
                                                className={clsx(
                                                    styles.TableHeadRowItem,
                                                    styles.ClassificationHeader,
                                                    styles.StickyClassificationHeader,
                                                )}
                                                style={stickyCol3Style}
                                            >
                                                Classification
                                            </th>
                                        )}
                                        {periodHeaders.map((header: string) => (
                                            <th key={header} className={styles.TableHeadRowItem}>
                                                {header.trim().replace(' ', "'")}
                                            </th>
                                        ))}
                                        <th className={clsx(styles.TableHeadRowItem, styles.TotalHeader)}>Total</th>
                                    </tr>
                                </thead>
                            </table>
                        </div>

                        <table className={styles.Table} style={tableStyle}>
                            {renderColGroup()}
                            <tbody className={styles.TableBody}>
                                {renderAggregateRow('Opening Balance', aggregates.opening, false, false, 'first')}
                            </tbody>
                        </table>

                        {layout.sections.map((section) => (
                            <table key={section.band} className={styles.Table} style={tableStyle}>
                                {renderColGroup()}
                                <tbody className={styles.TableBody}>
                                    {renderSectionBand(section.band)}
                                    {section.groupRows.map((groupRow) => renderGroupRow(groupRow, true))}
                                    {renderNetRow(section.netLabel, section.netAmountsMap)}
                                </tbody>
                            </table>
                        ))}

                        {layout.specialRows.map((special) => (
                            <table key={`special-${special.kind}`} className={styles.Table} style={tableStyle}>
                                {renderColGroup()}
                                <tbody className={styles.TableBody}>{renderGroupRow(special)}</tbody>
                            </table>
                        ))}

                        <table className={styles.Table} style={tableStyle}>
                            {renderColGroup()}
                            <tbody className={styles.TableBody}>
                                {renderAggregateRow('Net Cashflow', aggregates.netCashflow, true, true)}
                                {renderAggregateRow('Closing Balance', aggregates.closing, false, false, 'last')}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {drawer && (
                <EntityDrillDownDrawer
                    entityName={drawer.entityName}
                    entityId={drawer.entityId}
                    transactions={drawer.transactions}
                    loading={drawer.loading}
                    onClose={() => setDrawer(null)}
                    canEdit={canEditDrawer}
                    orgEntities={orgEntities}
                    rationaleOptions={rationaleOptions}
                    onSave={handleDrawerSave}
                />
            )}
            {editMode && (
                <EditClassificationSaveBar stagedCount={pendingCount} saving={saving} onCancel={handleCancel} onSave={handleSave} />
            )}
        </div>
    );
}

const FORMULA_HOVER = (
    <div className={styles.FormulaContainer}>
        <div className={styles.Label}>N</div>=<div className={styles.Label}>C</div>-<div className={styles.Label}>B</div>
    </div>
);

export default FinancialsFromBankingV2;
